import {
  HttpErrorResponse,
  HttpEvent,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse
} from "@angular/common/http";
import { inject } from "@angular/core";

import { catchError, from, Observable, of, switchMap, tap, throwError } from "rxjs";

import { OfflineAutosaveService, OfflineParticipationRead } from "./offline-autosave.service";
import { OFFLINE_BYPASS, OFFLINE_OPTIMISTIC_ENTRY } from "./offline-http.context";
import { OfflineIdentityService } from "./offline-identity.service";
import { OfflineModeStateService } from "./offline-mode-state.service";
import { OfflineQueueKind, OfflineSnapshotTags } from "./offline-mode.types";
import { OfflineStoreService } from "./offline-store.service";
import { OfflineSyncService } from "./offline-sync.service";

/** Detalhe do conteúdo (páginas, blocos, perguntas): base de qualquer atribuição aberta. */
const DETAIL_GET = /\/by-user\/detail\/([^/]+)$/;
/** Leituras de uma atribuição pelo `idAssignment` da query. */
const ASSIGNMENT_GET = /\/content-assignment\/(participation|lesson-status)$/;
/** Envios de resposta da atribuição (GET lista, POST registra). */
const ANSWERS_PATH = /\/content-assignment\/([^/]+)\/question-answers$/;
/**
 * DEV-1023: pessoas da atribuição (alunos da turma) que a Distribuição distribui — guardadas para o
 * quadro responder sem rede, como os envios (decisão de produto de 2026-10-21).
 */
const MEMBERS_GET = /\/content-assignment\/([^/]+)\/members$/;
/** Listagens que só o Modo Offline guarda (DEV-966). */
const MODE_GET =
  /\/content-assignment\/(professor\/assignments|manager\/assignments|overview|counters|activity)(\/|$)/;

const FINISHED_STATUS = 2;
const LESSON_STATUS_WRITE = /\/content-assignment\/lesson-status$/;
const CONTENT_STATUS_WRITE = /\/content-assignment\/content-status$/;
const PARTICIPATION_PATH = /\/content-assignment\/participation$/;

interface LessonStatusWriteBody {
  idAssignment: string;
  idPageContentBlocks: string[];
  inStatus: number;
}

interface ContentStatusWriteBody {
  idAssignment: string;
  inStatus: number;
}

interface LessonStatusSnapshot {
  contentInStatus: number;
  lessons: { idPageContentBlock: string; inStatus: number }[];
}

interface AnswersSnapshot {
  idAssignment: string;
  submissions: unknown[];
}

interface QuestionAnswerWriteBody {
  idContentBlockQuestion?: string;
  answer?: { attachments?: unknown[] };
}

interface WriteTarget {
  kind: Exclude<OfflineQueueKind, "offline-mode">;
  idAssignment: string;
}

interface OfflineContext {
  owner: string;
  state: OfflineModeStateService;
  store: OfflineStoreService;
  sync: OfflineSyncService;
  autosave: OfflineAutosaveService;
}

function pathOf(url: string): string {
  try {
    return new URL(url, globalThis.location?.origin ?? "http://localhost").pathname;
  } catch {
    return url.split("?")[0];
  }
}

function queryParam(req: HttpRequest<unknown>, name: string): string | null {
  const fromParams = req.params.get(name);
  if (fromParams) return fromParams;
  try {
    return new URL(
      req.urlWithParams,
      globalThis.location?.origin ?? "http://localhost"
    ).searchParams.get(name);
  } catch {
    return null;
  }
}

/** Chave estável do snapshot: caminho + params ordenados e decodificados. */
export function snapshotKey(method: string, url: string, params?: Record<string, string>): string {
  try {
    const parsed = new URL(url, globalThis.location?.origin ?? "http://localhost");
    const entries: [string, string][] = [];
    if (params) entries.push(...Object.entries(params));
    else parsed.searchParams.forEach((value, key) => entries.push([key, value]));
    entries.sort(([a], [b]) => a.localeCompare(b));
    const query = entries.map(([key, value]) => `${key}=${value}`).join("&");
    return `${method.toUpperCase()} ${parsed.pathname}${query ? `?${query}` : ""}`;
  } catch {
    return `${method.toUpperCase()} ${url}`;
  }
}

/** Etiquetas de um GET guardável; `null` quando a leitura não entra no snapshot. */
function classifyGet(
  req: HttpRequest<unknown>,
  path: string,
  modeEnabled: boolean
): OfflineSnapshotTags | null {
  const detail = DETAIL_GET.exec(path);
  if (detail) {
    return {
      scope: "autosave",
      idAssignment: null,
      idContentTemplate: decodeURIComponent(detail[1])
    };
  }

  if (ASSIGNMENT_GET.test(path)) {
    const idAssignment = queryParam(req, "idAssignment");
    return idAssignment ? { scope: "autosave", idAssignment, idContentTemplate: null } : null;
  }

  const answers = ANSWERS_PATH.exec(path) ?? MEMBERS_GET.exec(path);
  if (answers) {
    return {
      scope: "autosave",
      idAssignment: decodeURIComponent(answers[1]),
      idContentTemplate: null
    };
  }

  if (modeEnabled && MODE_GET.test(path)) {
    return { scope: "mode", idAssignment: null, idContentTemplate: null };
  }

  return null;
}

function classifyWrite(req: HttpRequest<unknown>, path: string): WriteTarget | null {
  if (req.method !== "POST") return null;

  if (LESSON_STATUS_WRITE.test(path) || CONTENT_STATUS_WRITE.test(path)) {
    const body = req.body as Partial<ContentStatusWriteBody> | null;
    if (!body?.idAssignment) return null;
    return {
      kind: LESSON_STATUS_WRITE.test(path) ? "set-lesson-status" : "set-content-status",
      idAssignment: body.idAssignment
    };
  }

  const answers = ANSWERS_PATH.exec(path);
  if (answers) {
    return { kind: "submit-question-answer", idAssignment: decodeURIComponent(answers[1]) };
  }

  return null;
}

function isNetworkFailure(error: unknown): boolean {
  return error instanceof HttpErrorResponse && error.status === 0;
}

function serveFromSnapshot<T>(
  ctx: OfflineContext,
  key: string,
  url: string,
  fallback: unknown
): Observable<HttpEvent<T>> {
  return from(ctx.store.getSnapshot<T>(ctx.owner, key)).pipe(
    switchMap((cached) =>
      cached === null
        ? throwError(() => fallback)
        : of(new HttpResponse<T>({ status: 200, body: cached, url }))
    )
  );
}

/**
 * DEV-966 / DEV-1020 — leitura e escrita offline das atribuições.
 *
 * - Autosave (sempre, com ou sem o Modo Offline): as leituras de uma atribuição aberta (detalhe do
 *   conteúdo, participação, status das aulas, envios de resposta, pessoas da Distribuição) vão à
 *   rede e gravam o snapshot;
 *   sem rede, respondem dele. As escritas de progresso e de resposta vão à rede; sem rede, entram
 *   na fila, aplicam o efeito no snapshot (a tela relê logo depois) e recebem resposta sintética.
 * - Modo Offline ligado: também guarda as listagens (lista branca `MODE_GET`) e só aceita escrita
 *   da atribuição escolhida — as outras atravessam e o servidor recusa com 423.
 *
 * Sem dono (sessão sem `sub`) ou com `OFFLINE_BYPASS`, é transparente.
 */
export const offlineHttpInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.context.get(OFFLINE_BYPASS)) return next(req);

  const owner = inject(OfflineIdentityService).owner();
  if (!owner) return next(req);

  const state = inject(OfflineModeStateService);
  const path = pathOf(req.url);

  if (req.method === "GET") {
    const tags = classifyGet(req, path, state.enabled());
    if (!tags) return next(req);
    return interceptCacheableGet(req, next, contextOf(owner, state), tags, path);
  }

  const target = classifyWrite(req, path);
  if (!target) return next(req);
  // Modo ligado em outra atribuição: atravessa e o servidor recusa com 423 (DEV-966).
  if (state.enabled() && !state.isSelectedAssignment(target.idAssignment)) return next(req);
  return interceptWrite(req, next, contextOf(owner, state), target);
};

function contextOf(owner: string, state: OfflineModeStateService): OfflineContext {
  return {
    owner,
    state,
    store: inject(OfflineStoreService),
    sync: inject(OfflineSyncService),
    autosave: inject(OfflineAutosaveService)
  };
}

function interceptCacheableGet(
  req: HttpRequest<unknown>,
  next: (request: HttpRequest<unknown>) => Observable<HttpEvent<unknown>>,
  ctx: OfflineContext,
  tags: OfflineSnapshotTags,
  path: string
): Observable<HttpEvent<unknown>> {
  const key = snapshotKey(req.method, req.urlWithParams);

  if (!ctx.state.isOnline()) {
    return serveFromSnapshot(ctx, key, req.urlWithParams, offlineError(req.urlWithParams));
  }

  return next(req).pipe(
    tap((event) => {
      if (!(event instanceof HttpResponse) || !event.ok) return;
      const saved = ctx.store.setSnapshot(ctx.owner, key, event.body, tags);
      if (PARTICIPATION_PATH.test(path)) {
        void saved.then(() =>
          ctx.autosave.recordParticipation(ctx.owner, event.body as OfflineParticipationRead)
        );
      }
    }),
    catchError((error: unknown) =>
      isNetworkFailure(error)
        ? serveFromSnapshot(ctx, key, req.urlWithParams, error)
        : throwError(() => error)
    )
  );
}

function interceptWrite(
  req: HttpRequest<unknown>,
  next: (request: HttpRequest<unknown>) => Observable<HttpEvent<unknown>>,
  ctx: OfflineContext,
  target: WriteTarget
): Observable<HttpEvent<unknown>> {
  const queueLocally = (): Observable<HttpEvent<unknown>> =>
    from(applyOptimistic(ctx, req, target)).pipe(
      switchMap(() =>
        ctx.sync.enqueue({
          kind: target.kind,
          method: "POST",
          url: req.urlWithParams,
          body: req.body,
          idAssignment: target.idAssignment
        })
      ),
      switchMap(() => of(syntheticResponse(req, target)))
    );

  if (!ctx.state.isOnline()) return queueLocally();

  return next(req).pipe(
    catchError((error: unknown) =>
      isNetworkFailure(error) ? queueLocally() : throwError(() => error)
    )
  );
}

function syntheticResponse(req: HttpRequest<unknown>, target: WriteTarget): HttpResponse<unknown> {
  const url = req.urlWithParams;

  if (target.kind === "submit-question-answer") {
    const body = req.body as QuestionAnswerWriteBody | null;
    return new HttpResponse({
      status: 201,
      url,
      body: {
        idAssignment: target.idAssignment,
        idContentBlockQuestion: body?.idContentBlockQuestion ?? "",
        idExecutorReference: "",
        dtInserted: new Date().toISOString(),
        inStatus: FINISHED_STATUS,
        attachmentCount: body?.answer?.attachments?.length ?? 0
      }
    });
  }

  const body = req.body as Partial<LessonStatusWriteBody & ContentStatusWriteBody> | null;
  return new HttpResponse({
    status: 200,
    url,
    body:
      target.kind === "set-lesson-status"
        ? {
            idAssignment: target.idAssignment,
            inStatus: body?.inStatus ?? FINISHED_STATUS,
            affectedCount: body?.idPageContentBlocks?.length ?? 0
          }
        : { idAssignment: target.idAssignment, inStatus: body?.inStatus ?? FINISHED_STATUS }
  });
}

/**
 * Espelha a escrita no snapshot para a releitura que a tela faz logo depois enxergar o efeito
 * mesmo sem rede — e para quem fechar e reabrir a atribuição antes de sincronizar (CA 03). O
 * servidor segue a única fonte de verdade: ao sincronizar, a próxima leitura em rede substitui.
 */
async function applyOptimistic(
  ctx: OfflineContext,
  req: HttpRequest<unknown>,
  target: WriteTarget
): Promise<void> {
  if (target.kind === "submit-question-answer") {
    await appendAnswerToSnapshot(ctx, req, target.idAssignment);
    return;
  }

  const body = req.body as Partial<LessonStatusWriteBody & ContentStatusWriteBody> | null;
  const isLessonStatus = target.kind === "set-lesson-status";
  const tags: OfflineSnapshotTags = {
    scope: "autosave",
    idAssignment: target.idAssignment,
    idContentTemplate: null
  };

  const readUrl = req.url.replace(/\/(lesson-status|content-status)$/, "/lesson-status");
  const key = snapshotKey("GET", readUrl, { idAssignment: target.idAssignment });
  const current = await ctx.store.getSnapshot<LessonStatusSnapshot>(ctx.owner, key);
  const inStatus = body?.inStatus ?? FINISHED_STATUS;

  if (current) {
    const updated: LessonStatusSnapshot = isLessonStatus
      ? {
          ...current,
          lessons: current.lessons.map((lesson) =>
            (body?.idPageContentBlocks ?? []).includes(lesson.idPageContentBlock)
              ? { ...lesson, inStatus }
              : lesson
          )
        }
      : { ...current, contentInStatus: inStatus };
    await ctx.store.setSnapshot(ctx.owner, key, updated, tags);
  }

  if (isLessonStatus || inStatus !== FINISHED_STATUS) return;

  // Finalizar o conteúdo também fecha o período na participação (dtEnd), que a tela relê.
  const participationUrl = req.url.replace(/\/content-status$/, "/participation");
  const participationKey = snapshotKey("GET", participationUrl, {
    idAssignment: target.idAssignment
  });
  const participation = await ctx.store.getSnapshot<Record<string, unknown>>(
    ctx.owner,
    participationKey
  );
  if (participation) {
    await ctx.store.setSnapshot(
      ctx.owner,
      participationKey,
      { ...participation, dtEnd: new Date().toISOString() },
      tags
    );
  }
  await ctx.autosave.markFinished(ctx.owner, target.idAssignment);
}

async function appendAnswerToSnapshot(
  ctx: OfflineContext,
  req: HttpRequest<unknown>,
  idAssignment: string
): Promise<void> {
  const entry = req.context.get(OFFLINE_OPTIMISTIC_ENTRY);
  if (!entry) return;

  const key = snapshotKey("GET", req.url);
  const current = await ctx.store.getSnapshot<AnswersSnapshot>(ctx.owner, key);
  const updated: AnswersSnapshot = {
    idAssignment,
    submissions: [...(current?.submissions ?? []), entry]
  };
  await ctx.store.setSnapshot(ctx.owner, key, updated, {
    scope: "autosave",
    idAssignment,
    idContentTemplate: null
  });
}

function offlineError(url: string): HttpErrorResponse {
  return new HttpErrorResponse({ status: 0, statusText: "Offline", url });
}
